import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, finalize } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { friendlyError } from '../../utils/friendly-error';
import { PaginationComponent } from '../ui/pagination.component';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { UiStateComponent } from '../ui/ui-state.component';
import {
  DelegatedOwner,
  Employee,
  EmployeeFormPayload,
  EmployeePermissionMap,
  EmployeeRole,
  EmployeeService,
  NotificationBucket,
  NotificationBucketOption,
} from '../../services/employee.service';
import { SubscriptionService } from '../../services/subscription.service';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { EsDatePipe } from '../../shared/es-date.pipe';

type CreateType = 'employee' | 'owner' | 'super_admin';

type EmployeeForm = {
  name: string;
  last_name: string;
  email: string;
  phone_number: string;
  job_title: string;
  business_role: EmployeeRole;
  password: string;
  permissions: EmployeePermissionMap;
  notification_categories: NotificationBucket[];
};

type EmployeeAccessView = {
  count: number;
  modules: Array<{ key: string; label: string }>;
};

@Component({
  selector: 'app-empleados',
  standalone: true,
  imports: [EsDatePipe, CommonModule, FormsModule, PaginationComponent, UiSkeletonComponent, UiStateComponent, CooldownBubbleDirective],
  template: `
    <section class="employee-page paginated-module">
      <nav class="view-tabs" *ngIf="auth.canManageAdmins()" aria-label="Tipo de accesos administrativos">
        <button type="button" [class.active]="viewMode() === 'employees'" (click)="setViewMode('employees')">
          <i class="bi bi-person-badge"></i> Empleados
        </button>
        <button type="button" [class.active]="viewMode() === 'owners'" (click)="setViewMode('owners')">
          <i class="bi bi-person-vcard"></i> Propietarios delegados
        </button>
      </nav>

      <section class="summary-grid" *ngIf="viewMode() === 'employees'" aria-label="Resumen del equipo">
        <article><i class="bi bi-people"></i><div><span>Total del equipo</span><strong>{{ total() }}</strong></div></article>
        <article><i class="bi bi-person-check"></i><div><span>Activos en esta página</span><strong>{{ activeCount() }}</strong></div></article>
        <article><i class="bi bi-person-dash"></i><div><span>Inactivos en esta página</span><strong>{{ inactiveCount() }}</strong></div></article>
      </section>

      <ng-container *ngIf="viewMode() === 'employees'">
        <section class="employee-toolbar" aria-label="Filtros de sub-administradores">
          <label class="search-field">
            <span><i class="bi bi-search"></i> Buscar</span>
            <input
              type="search"
              [(ngModel)]="search"
              (ngModelChange)="scheduleSearch()"
              placeholder="Nombre o correo"
              autocomplete="off"
            >
          </label>
          <label>
            <span><i class="bi bi-activity"></i> Estado</span>
            <select [(ngModel)]="status" (change)="applyFilters()">
              <option value="">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </label>
          <div class="toolbar-actions">
            <button class="refresh-action" (click)="refresh()" [disabled]="loading()" cooldownBubble data-cooldown-key="empleados-refresh">
              <i class="bi bi-arrow-clockwise" [class.spin]="loading()"></i>
              {{ loading() ? 'Actualizando' : 'Actualizar' }}
            </button>
            <button *ngIf="canCreate()" class="primary-action toolbar-create" (click)="openCreate('employee')">
              <i class="bi bi-person-plus"></i><span>Nuevo sub-administrador</span>
            </button>
          </div>
        </section>

        <p class="feedback error" *ngIf="error()" role="alert"><i class="bi bi-exclamation-circle"></i>{{ error() }}</p>

        <app-ui-skeleton *ngIf="loading()" class="loading-state" variant="cards" [count]="6" [compact]="true" label="Cargando sub-administradores"></app-ui-skeleton>

        <section class="employee-cards" *ngIf="!loading()">
          <article class="employee-card" *ngFor="let item of employees(); trackBy: trackEmployee" [class.inactive]="!item.is_active">
            <header class="person">
              <span class="avatar">{{ initials(item.user.name) }}</span>
              <div class="identity">
                <strong>{{ item.user.name }}</strong>
                <small>{{ item.user.email }}</small>
              </div>
              <span class="status-badge" [class.off]="!item.is_active"><i></i>{{ item.is_active ? 'Activo' : 'Inactivo' }}</span>
            </header>

            <div class="role-line">
              <span><i class="bi bi-briefcase"></i>{{ item.job_title || 'Cargo no definido' }}</span>
              <b>{{ roleLabel(item.business_role) }}</b>
            </div>

            <dl class="employee-meta">
              <div><dt>Teléfono</dt><dd>{{ item.user.phone_number || 'No registrado' }}</dd></div>
              <div><dt>Último acceso</dt><dd>{{ item.user.last_login_at ? (item.user.last_login_at | esDate:'short') : 'Nunca' }}</dd></div>
              <div><dt>Creado</dt><dd>{{ item.user.created_at | esDate:'mediumDate' }}</dd></div>
              <div><dt>Seguridad</dt><dd>{{ item.user.force_password_change ? 'Cambio pendiente' : 'Contraseña vigente' }}</dd></div>
            </dl>

            <section class="access-summary">
              <header><span>Accesos delegados</span><b>{{ employeeAccess(item).count }} permisos</b></header>
              <div>
                <span *ngFor="let module of employeeAccess(item).modules; trackBy: trackModule"><i [class]="moduleIcon(module.key)"></i>{{ module.label }}</span>
                <small *ngIf="!employeeAccess(item).modules.length">Sin módulos asignados</small>
              </div>
            </section>

            <footer class="card-actions">
              <button *ngIf="canEdit()" (click)="edit(item)" [disabled]="busyId() === item.user_id" title="Editar datos y accesos" aria-label="Editar datos y accesos">
                <i class="bi bi-pencil-square"></i>
              </button>
              <button *ngIf="canEdit()" class="status-action" [class.activate]="!item.is_active" (click)="toggle(item)" [disabled]="busyId() === item.user_id" [title]="item.is_active ? 'Desactivar acceso' : 'Activar acceso'" [attr.aria-label]="item.is_active ? 'Desactivar acceso' : 'Activar acceso'">
                <i [class]="item.is_active ? 'bi bi-person-slash' : 'bi bi-person-check'"></i>
              </button>
              <button *ngIf="canEdit()" (click)="reset(item)" [disabled]="busyId() === item.user_id" title="Enviar enlace de restablecimiento" aria-label="Enviar enlace de restablecimiento">
                <i class="bi bi-key"></i>
              </button>
              <button *ngIf="canDelete()" class="danger" (click)="remove(item)" [disabled]="busyId() === item.user_id" title="Archivar acceso" aria-label="Archivar acceso">
                <i class="bi bi-trash3"></i>
              </button>
            </footer>
          </article>

          <app-ui-state class="employees-empty" *ngIf="!employees().length" tone="empty" title="No encontramos sub-administradores" message="Ajusta la búsqueda o el filtro de estado."></app-ui-state>
        </section>

        <app-pagination [page]="page()" [pages]="pages()" [total]="total()" [limit]="pageSize" [disabled]="loading()" itemLabel="sub-administradores" ariaLabel="Paginación de sub-administradores" (pageChange)="goTo($event)"></app-pagination>
      </ng-container>

      <ng-container *ngIf="viewMode() === 'owners'">
        <section class="employee-toolbar" aria-label="Filtros de propietarios delegados">
          <label class="search-field">
            <span><i class="bi bi-search"></i> Buscar</span>
            <input type="search" [(ngModel)]="ownerSearch" (ngModelChange)="scheduleOwnerSearch()" placeholder="Nombre o correo" autocomplete="off">
          </label>
          <label>
            <span><i class="bi bi-activity"></i> Estado</span>
            <select [(ngModel)]="ownerStatusFilter" (change)="applyOwnerFilters()">
              <option value="">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </label>
          <div class="toolbar-actions">
            <button class="refresh-action" (click)="refreshOwners()" [disabled]="ownersLoading()">
              <i class="bi bi-arrow-clockwise" [class.spin]="ownersLoading()"></i>
              {{ ownersLoading() ? 'Actualizando' : 'Actualizar' }}
            </button>
            <button class="primary-action toolbar-create" (click)="openCreate('owner')">
              <i class="bi bi-person-plus"></i><span>Delegar propietario</span>
            </button>
            <button *ngIf="auth.currentUser()?.role === 'super_admin'" class="primary-action toolbar-create" (click)="openCreate('super_admin')">
              <i class="bi bi-shield-plus"></i><span>Delegar superadministrador</span>
            </button>
          </div>
        </section>

        <p class="feedback error" *ngIf="ownersError()" role="alert"><i class="bi bi-exclamation-circle"></i>{{ ownersError() }}</p>

        <app-ui-skeleton *ngIf="ownersLoading()" class="loading-state" variant="cards" [count]="4" [compact]="true" label="Cargando propietarios delegados"></app-ui-skeleton>

        <section class="employee-cards" *ngIf="!ownersLoading()">
          <article class="employee-card" *ngFor="let item of owners(); trackBy: trackOwner" [class.inactive]="!item.is_active">
            <header class="person">
              <span class="avatar">{{ initials(item.user.name) }}</span>
              <div class="identity">
                <strong>{{ item.user.name }}</strong>
                <small>{{ item.user.email }}</small>
              </div>
              <span class="status-badge" [class.off]="!item.is_active"><i></i>{{ item.is_active ? 'Activo' : 'Inactivo' }}</span>
            </header>

            <div class="role-line">
              <span><i class="bi bi-briefcase"></i>{{ item.job_title || 'Cargo no definido' }}</span>
              <b>Propietario delegado</b>
            </div>

            <dl class="employee-meta">
              <div><dt>Teléfono</dt><dd>{{ item.user.phone_number || 'No registrado' }}</dd></div>
              <div><dt>Último acceso</dt><dd>{{ item.user.last_login_at ? (item.user.last_login_at | esDate:'short') : 'Nunca' }}</dd></div>
              <div><dt>Creado</dt><dd>{{ item.user.created_at | esDate:'mediumDate' }}</dd></div>
              <div><dt>Seguridad</dt><dd>{{ item.user.force_password_change ? 'Cambio pendiente' : 'Contraseña vigente' }}</dd></div>
            </dl>

            <p class="owner-note"><i class="bi bi-info-circle"></i>Mismo acceso operativo que el propietario (pagos, renovaciones, espacios, reservas, estadísticas). No puede administrar accesos administrativos.</p>

            <footer class="card-actions">
              <button class="status-action" [class.activate]="!item.is_active" (click)="toggleOwner(item)" [disabled]="ownerBusyId() === item.user_id" [title]="item.is_active ? 'Desactivar acceso' : 'Activar acceso'" [attr.aria-label]="item.is_active ? 'Desactivar acceso' : 'Activar acceso'">
                <i [class]="item.is_active ? 'bi bi-person-slash' : 'bi bi-person-check'"></i>
              </button>
              <button (click)="resetOwnerPwd(item)" [disabled]="ownerBusyId() === item.user_id" title="Enviar enlace de restablecimiento" aria-label="Enviar enlace de restablecimiento">
                <i class="bi bi-key"></i>
              </button>
              <button class="danger" (click)="removeOwner(item)" [disabled]="ownerBusyId() === item.user_id" title="Eliminar acceso" aria-label="Eliminar acceso">
                <i class="bi bi-trash3"></i>
              </button>
            </footer>
          </article>

          <app-ui-state class="employees-empty" *ngIf="!owners().length" tone="empty" title="No has delegado propietarios" message="Solo tú (el propietario registrado) puedes delegar acceso completo a otra persona."></app-ui-state>
        </section>

        <app-pagination [page]="ownersPage()" [pages]="ownersPages()" [total]="ownersTotal()" [limit]="pageSize" [disabled]="ownersLoading()" itemLabel="propietarios delegados" ariaLabel="Paginación de propietarios delegados" (pageChange)="goToOwnerPage($event)"></app-pagination>
      </ng-container>
    </section>

    <div class="employee-overlay" *ngIf="showForm()" (click)="close()">
      <form class="employee-modal" (click)="$event.stopPropagation()" (ngSubmit)="save()">
        <header class="modal-header">
          <div>
            <span class="eyebrow">{{ editingId() ? 'ACTUALIZAR ACCESO' : createTypeEyebrow() }}</span>
            <h3>{{ editingId() ? 'Editar sub-administrador' : createTypeTitle() }}</h3>
            <p>{{ createType() === 'super_admin' ? 'Tendrá acceso administrativo total a toda la plataforma, no solo a este negocio.' : 'Los cambios de acceso invalidan las sesiones anteriores para proteger el negocio.' }}</p>
          </div>
          <button type="button" class="close" (click)="close()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>

        <div class="modal-body">
          <section class="form-section" *ngIf="!editingId() && auth.canManageAdmins()">
            <header><i class="bi bi-diagram-3"></i><div><h4>Tipo de acceso</h4><p>Cada tipo tiene un alcance distinto -- elige con cuidado.</p></div></header>
            <div class="form-grid">
              <label class="full">
                Tipo
                <select [ngModel]="createType()" name="create_type" (ngModelChange)="onCreateTypeChange($any($event))">
                  <option value="employee">Empleado (permisos delegados)</option>
                  <option value="owner">Propietario delegado (acceso operativo total)</option>
                  <option value="super_admin" *ngIf="auth.currentUser()?.role === 'super_admin'">Superadministrador (control total de la plataforma)</option>
                </select>
              </label>
            </div>
          </section>

          <p class="permission-warning" *ngIf="!editingId() && createType() === 'owner'"><i class="bi bi-exclamation-triangle"></i>Tendrá el mismo acceso operativo que tú (pagos, renovaciones, espacios, reservas, estadísticas), pero nunca podrá administrar accesos administrativos de este negocio.</p>
          <p class="permission-warning" *ngIf="!editingId() && createType() === 'super_admin'"><i class="bi bi-exclamation-triangle"></i>Tendrá control administrativo de TODA la plataforma TuSala, no solo de este negocio. Se le enviará un enlace para que establezca su propia contraseña segura.</p>

          <section class="form-section">
            <header><i class="bi bi-person-vcard"></i><div><h4>Información personal</h4><p>Datos de identificación y contacto.</p></div></header>
            <div class="form-grid">
              <label>Nombre<input [(ngModel)]="form.name" name="name" required maxlength="120" placeholder="Ej. Juan Pérez" [class.invalid]="invalidFields['name']"></label>
              <label *ngIf="!editingId() && createType() === 'employee'">Apellidos<input [(ngModel)]="form.last_name" name="last_name" maxlength="120" placeholder="Ej. López" [class.invalid]="invalidFields['last_name']"></label>
              <label>Correo<input type="email" [(ngModel)]="form.email" name="email" required maxlength="100" placeholder="nombre@ejemplo.com" [class.invalid]="invalidFields['email']"></label>
              <label>Teléfono<input [(ngModel)]="form.phone_number" name="phone" maxlength="30" placeholder="Ej. +503 71234567" [class.invalid]="invalidFields['phone_number']"></label>
              <label *ngIf="createType() !== 'super_admin'">Cargo<input [(ngModel)]="form.job_title" name="job" maxlength="100" placeholder="Ej. Recepcionista" [class.invalid]="invalidFields['job_title']"></label>
              <label *ngIf="canDelegateCurrent() && createType() === 'employee'">
                Plantilla de acceso
                <select [(ngModel)]="form.business_role" name="role" (change)="applyRole()">
                  <option value="manager">Gerente</option>
                  <option value="receptionist">Recepción</option>
                  <option value="analyst">Analista</option>
                </select>
              </label>
              <label *ngIf="!editingId() && createType() !== 'super_admin'" class="full">
                Acceso seguro
                <div class="temp-note">Se enviará un enlace por correo para que el usuario establezca su propia contraseña segura. El administrador no podrá verla.</div>
              </label>
            </div>
          </section>

          <section class="form-section permission-section" *ngIf="canDelegateCurrent() && createType() === 'employee'">
            <header>
              <i class="bi bi-shield-check"></i>
              <div><h4>Delegación de permisos</h4><p>Solo aparecen funciones que existen actualmente en el panel y cuentan con validación de backend.</p></div>
              <span class="permission-total">{{ permissionCount(form.permissions) }} seleccionados</span>
            </header>
            <p class="permission-warning"><i class="bi bi-exclamation-triangle"></i>Las acciones de eliminación, restauración y administración de accesos son críticas.</p>
            <div class="permission-grid">
              <article *ngFor="let module of moduleNames()" [class.enabled]="moduleEnabled(module)">
                <header>
                  <span><i [class]="moduleIcon(module)"></i>{{ moduleLabel(module) }}</span>
                  <button type="button" (click)="toggleModule(module)">{{ moduleEnabled(module) ? 'Quitar todo' : 'Seleccionar' }}</button>
                </header>
                <div>
                  <label *ngFor="let action of catalog()[module]" [class.critical]="criticalAction(action)">
                    <input type="checkbox" [checked]="selected(module, action)" (change)="togglePermission(module, action, $any($event.target).checked)">
                    <span>{{ actionLabel(action) }}</span>
                  </label>
                </div>
              </article>
            </div>
          </section>

          <section class="form-section notification-section" *ngIf="createType() === 'employee'">
            <header>
              <i class="bi bi-envelope-check"></i>
              <div><h4>Avisos por correo</h4><p>Qué grupos de avisos del negocio le llegan por correo. Siempre puede consultarlos todos dentro del panel, elija lo que elija aquí.</p></div>
            </header>
            <div class="notification-bucket-grid">
              <label *ngFor="let bucket of notificationBuckets()" class="notification-bucket" [class.enabled]="notificationBucketSelected(bucket.key)">
                <input type="checkbox" [checked]="notificationBucketSelected(bucket.key)" (change)="toggleNotificationBucket(bucket.key, $any($event.target).checked)">
                <div><strong>{{ bucket.label }}</strong><span>{{ bucket.description }}</span></div>
              </label>
            </div>
          </section>

          <p class="feedback error" *ngIf="formError()" role="alert"><i class="bi bi-exclamation-circle"></i>{{ formError() }}</p>
        </div>

        <footer class="modal-actions">
          <button type="button" class="secondary" (click)="close()" [disabled]="saving()">Cancelar</button>
          <button type="submit" class="save-action" [disabled]="saving()" cooldownBubble data-cooldown-key="empleados-save">
            <i class="bi" [class.bi-check2-circle]="!saving()" [class.bi-arrow-clockwise]="saving()" [class.spin]="saving()"></i>
            {{ saving() ? 'Guardando' : (editingId() ? 'Guardar cambios' : 'Crear acceso') }}
          </button>
        </footer>
      </form>
    </div>
  `,
  styles: [`
    :host{display:block}.employee-page{display:grid;gap:18px;color:var(--ts-text)}.employee-hero{display:flex;justify-content:space-between;align-items:center;gap:24px;padding:24px 26px;border:1px solid var(--ts-border-navy-700-4);border-radius:18px;background:radial-gradient(circle at 90% 0,var(--ts-bg-blue-400-a18),transparent 38%),linear-gradient(145deg,var(--ts-bg-navy-850-5),var(--ts-bg-navy-900));box-shadow:0 18px 45px var(--ts-shadow-gray-1000-a18)}.eyebrow{display:inline-flex;align-items:center;gap:7px;color:var(--ts-text-blue-250);font-size:.68rem;font-weight:850;letter-spacing:.13em}.employee-hero h2{margin:7px 0 5px;color:var(--ts-text-blue-0);font-size:1.65rem}.employee-hero p,.modal-header p,.form-section header p{margin:0;color:var(--ts-text-sky-300-8);line-height:1.5}.primary-action,.refresh-action,.card-actions button,.pagination button,.modal-actions button,.permission-grid article header button{display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:36px;border:1px solid var(--ts-border-navy-700-5);border-radius:9px;font-weight:750;cursor:pointer}.primary-action,.save-action{padding:0 16px;color:var(--ts-on-primary);background:linear-gradient(135deg,var(--ts-primary),var(--ts-bg-indigo-350-6));border-color:var(--ts-primary-border-55)!important;box-shadow:0 10px 24px var(--ts-shadow-blue-400-a20)}button:disabled{cursor:not-allowed}.summary-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}.summary-grid article{display:flex;align-items:center;gap:13px;padding:15px 17px;border:1px solid var(--ts-border-strong);border-radius:13px;background:linear-gradient(145deg,var(--ts-bg-navy-850-12),var(--ts-bg-navy-900-3))}.summary-grid i{display:grid;place-items:center;width:39px;height:39px;border-radius:10px;color:var(--ts-text-blue-200-6);background:var(--ts-primary-soft-07);font-size:1.05rem}.summary-grid span,.summary-grid strong{display:block}.summary-grid span{color:var(--ts-text-sky-350-5);font-size:.7rem}.summary-grid strong{margin-top:3px;color:var(--ts-text);font-size:1.25rem}.employee-toolbar{display:flex;flex-wrap:wrap;align-items:end;gap:11px;padding:14px;border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-bg-navy-900-3)}.employee-toolbar label{display:grid;flex:1 1 170px;gap:6px}.employee-toolbar label.search-field{flex:2.4 1 240px}.toolbar-actions{display:flex;flex-wrap:wrap;flex:0 0 auto;gap:11px;margin-left:auto}.employee-toolbar label>span{display:flex;align-items:center;gap:6px;color:var(--ts-text-sky-350-6);font-size:.69rem;font-weight:800;text-transform:uppercase;letter-spacing:.06em}.employee-toolbar input,.employee-toolbar select{box-sizing:border-box;width:100%;height:41px;margin:0;padding:0 12px!important;color:var(--ts-text)!important;background:var(--ts-bg-navy-900-4)!important;border:1px solid var(--ts-border-navy-700-5)!important;border-radius:9px!important;box-shadow:none!important}.refresh-action{padding:0 14px;color:var(--ts-text-sky-100-2);background:var(--ts-bg-navy-850-13)}.feedback{display:flex;align-items:center;gap:8px;margin:0;padding:11px 14px;border-radius:10px}.error{color:var(--ts-text-red-100)!important;border:1px solid var(--ts-border-red-350-a35);background:var(--ts-bg-red-350-a10)!important}.loading-state{min-height:190px;display:flex;align-items:center;justify-content:center;gap:15px;border:1px dashed var(--ts-border-navy-700-5);border-radius:15px;background:var(--ts-bg-navy-900-a55-2)}.loading-state strong,.loading-state small{display:block}.loading-state small{margin-top:4px;color:var(--ts-text-sky-400-5)}.loader-ring{width:32px;height:32px;border:3px solid var(--ts-border-blue-250-a18);border-top-color:var(--ts-border-blue-300);border-radius:50%;animation:spin .75s linear infinite}.employee-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:15px}.employee-card{display:flex;flex-direction:column;min-width:0;padding:18px;border:1px solid var(--ts-border-strong);border-radius:15px;background:linear-gradient(145deg,var(--ts-bg-navy-850-12),var(--ts-bg-navy-900-3));box-shadow:0 12px 28px var(--ts-shadow-gray-1000-a14);transition:border-color .22s ease,transform .22s ease,box-shadow .22s ease}.employee-card:hover{transform:translateY(-2px);border-color:var(--ts-border-sky-600-2);box-shadow:0 16px 34px var(--ts-shadow-gray-1000-a20)}.employee-card.inactive{border-color:var(--ts-border-pink-700)}.person{display:flex;align-items:center;gap:11px}.avatar{display:grid;place-items:center;flex:0 0 44px;width:44px;height:44px;border-radius:12px;color:var(--ts-on-primary);background:linear-gradient(145deg,var(--ts-bg-blue-450-2),var(--ts-bg-indigo-400-2));font-weight:850}.identity{display:grid;min-width:0;flex:1}.identity strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--ts-text)}.identity small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--ts-text-sky-350-7)}.status-badge{display:inline-flex;align-items:center;gap:5px;padding:5px 8px;border:1px solid var(--ts-border-green-500-a28);border-radius:999px;color:var(--ts-success-text);background:var(--ts-bg-green-500-a10);font-size:.66rem;font-weight:800}.status-badge i{width:6px;height:6px;border-radius:50%;background:var(--ts-success);box-shadow:0 0 8px var(--ts-success)}.status-badge.off{color:var(--ts-text-red-150-4);border-color:var(--ts-border-red-350-a30);background:var(--ts-bg-red-350-a9)}.status-badge.off i{background:var(--ts-danger);box-shadow:0 0 8px var(--ts-danger)}.role-line{display:flex;justify-content:space-between;gap:10px;margin:16px 0 12px;padding:10px 11px;border-radius:9px;color:var(--ts-text-sky-300-9);background:var(--ts-bg-navy-950-a25);font-size:.73rem}.role-line span{display:flex;align-items:center;gap:6px}.role-line b{color:var(--ts-text-blue-150-2)}.employee-meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:0}.employee-meta>div{min-width:0;padding:10px;border:1px solid var(--ts-border-sky-300-a11);border-radius:9px;background:var(--ts-bg-navy-950-a17)}.employee-meta dt{color:var(--ts-text-sky-450-5);font-size:.62rem;font-weight:800;text-transform:uppercase;letter-spacing:.05em}.employee-meta dd{overflow:hidden;margin:4px 0 0;text-overflow:ellipsis;color:var(--ts-text-sky-100-3);font-size:.72rem;white-space:nowrap}.access-summary{margin-top:12px}.access-summary>header{display:flex;justify-content:space-between;gap:8px;color:var(--ts-text-sky-400-6);font-size:.67rem}.access-summary>header b{color:var(--ts-text-sky-200-6)}.access-summary>div{display:flex;flex-wrap:wrap;gap:5px;min-height:27px;margin-top:8px}.access-summary>div span{display:inline-flex;align-items:center;gap:4px;padding:4px 7px;border:1px solid var(--ts-border-blue-250-a18-2);border-radius:999px;color:var(--ts-text-blue-200-7);background:var(--ts-bg-blue-400-a7);font-size:.63rem}.access-summary small{color:var(--ts-text-sky-450-6)}.card-actions{display:flex;justify-content:flex-end;gap:6px;margin-top:auto;padding-top:15px}.card-actions button{display:grid;place-items:center;width:37px;height:37px;padding:0;color:var(--ts-text-sky-200-7);background:var(--ts-bg-navy-850-13);font-size:.85rem}.card-actions button:hover{border-color:var(--ts-border-sky-550-2);color:var(--ts-text)}.card-actions .activate{color:var(--ts-text-green-300);border-color:var(--ts-border-green-500-a30);background:var(--ts-bg-green-500-a9)}.card-actions .danger{color:var(--ts-text-red-150-4);border-color:var(--ts-border-red-350-a28);background:var(--ts-bg-red-350-a8)}.employees-empty{grid-column:1/-1}.pagination{display:flex;justify-content:center;align-items:center;gap:13px}.pagination button{padding:0 12px;color:var(--ts-text-sky-150-11);background:var(--ts-bg-navy-850-13)}.pagination span{color:var(--ts-text-sky-350-8);font-size:.75rem}.pagination strong{color:var(--ts-text-on-accent-13)}.employee-overlay{position:fixed;inset:0;z-index:3000;display:grid;place-items:center;padding:20px;background:var(--ts-bg-navy-950-a78);backdrop-filter:blur(10px)}.modal-actions{display:flex;justify-content:flex-end;gap:12px;padding:14px 36px;border-top:1px solid var(--ts-border-strong);align-items:center;flex-wrap:nowrap}.modal-actions .secondary{min-width:100px;margin-right:8px}.modal-actions .save-action{min-width:120px;margin-left:8px}.employee-modal input.invalid, .employee-modal select.invalid, .employee-modal textarea.invalid { border-color:var(--ts-border-red-350-3)!important; box-shadow:0 0 0 4px var(--ts-shadow-red-350-a6); }
  .employee-toolbar .primary-action, .employee-toolbar .toolbar-create, .employee-toolbar .refresh-action { display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:8px 12px; width:auto; min-width:0; max-width:none; flex:0 0 auto; white-space:nowrap; }
  .employee-toolbar .toolbar-create span { white-space:nowrap; }
  /* Ensure modal buttons don't stretch on larger screens */
  .modal-actions button{ flex:0 0 auto; padding:8px 12px; }
  /* Primary/save action with consistent sizing */
  .modal-actions .save-action, .primary-action { padding:8px 12px; min-width:0; }
  /* Keep mobile behavior: in small screens buttons may expand to full width (kept later in media queries) */
  .employee-toolbar .toolbar-create span { white-space: nowrap; }
.employee-modal{width:min(980px,100%);max-height:94vh;display:flex;flex-direction:column;overflow:hidden;border:1px solid var(--ts-border-navy-700-5);border-radius:19px;background:var(--ts-bg-navy-900);box-shadow:0 30px 80px var(--ts-shadow-gray-1000-a48)}.modal-header{display:flex;justify-content:space-between;gap:18px;padding:22px 24px;border-bottom:1px solid var(--ts-border-strong);background:var(--ts-bg-navy-850-14)}.modal-header h3{margin:5px 0;color:var(--ts-text-blue-0);font-size:1.35rem}.modal-header p{font-size:.76rem}.close{width:40px;height:40px;border:1px solid var(--ts-border-navy-650-2)!important;border-radius:10px;color:var(--ts-text-sky-200-2);background:var(--ts-bg-navy-800-2);cursor:pointer}.modal-body{display:grid;gap:15px;overflow:auto;padding:20px 24px}.form-section{border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-bg-navy-850-15)}.form-section>header{display:flex;align-items:center;gap:10px;padding:15px 17px;border-bottom:1px solid var(--ts-border-sky-300-a12)}.form-section>header>i{display:grid;place-items:center;width:36px;height:36px;border-radius:9px;color:var(--ts-text-blue-200-8);background:var(--ts-bg-blue-400-a10)}.form-section h4{margin:0;color:var(--ts-text)}.form-section header p{font-size:.69rem}.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;padding:16px}.form-grid label{display:grid;gap:6px;color:var(--ts-text-sky-200-8);font-size:.72rem;font-weight:750}.form-grid .full{grid-column:1/-1}.form-grid input,.form-grid select{box-sizing:border-box;width:100%;min-height:41px;padding:8px 11px!important;color:var(--ts-text)!important;background:var(--ts-bg-navy-900-4)!important;border:1px solid var(--ts-border-navy-700-5)!important;border-radius:9px!important;box-shadow:none!important}.form-grid small{color:var(--ts-text-subtle);font-weight:500}.permission-section>header{position:relative}.permission-total{margin-left:auto;padding:5px 9px;border-radius:999px;color:var(--ts-text-blue-200);background:var(--ts-primary-soft-055);font-size:.67rem}.permission-warning{display:flex;align-items:center;gap:7px;margin:13px 16px 0;padding:10px 12px;border:1px solid var(--ts-border-orange-400-a28);border-radius:9px;color:var(--ts-text-amber-300);background:var(--ts-bg-orange-400-a7);font-size:.7rem}.permission-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:13px 16px 16px}.permission-grid article{overflow:hidden;border:1px solid var(--ts-border-sky-300-a14);border-radius:11px;background:var(--ts-bg-navy-950-a18)}.permission-grid article.enabled{border-color:var(--ts-border-blue-400-a38-2)}.permission-grid article>header{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:10px 11px;border-bottom:1px solid var(--ts-border-sky-300-a10)}.permission-grid article>header span{display:flex;align-items:center;gap:7px;color:var(--ts-text-on-accent-14);font-size:.75rem;font-weight:800}.permission-grid article>header i{color:var(--ts-text-blue-300-5)}.permission-grid article header button{min-height:28px;padding:0 8px;color:var(--ts-text-sky-250-8);background:var(--ts-bg-navy-850-13);font-size:.6rem}.permission-grid article>div{display:flex;flex-wrap:wrap;gap:7px;padding:10px}.permission-grid label{display:flex;align-items:center;gap:6px;padding:6px 8px;border:1px solid var(--ts-border-sky-300-a13);border-radius:8px;color:var(--ts-text-sky-300);background:var(--ts-bg-navy-850-a55);font-size:.67rem;cursor:pointer}.permission-grid label:has(input:checked){color:var(--ts-text-on-accent-15);border-color:var(--ts-border-blue-400-a42);background:var(--ts-primary-soft-07)}.permission-grid label.critical:has(input:checked){color:var(--ts-text-orange-200-2);border-color:var(--ts-border-orange-400-a42);background:var(--ts-bg-orange-400-a9)}.permission-grid input{width:14px;height:14px;margin:0;accent-color:var(--ts-accent-blue-400)}.modal-actions{display:flex;justify-content:flex-end;gap:9px;padding:15px 24px;border-top:1px solid var(--ts-border-strong);background:var(--ts-bg-elevated)}.modal-actions button{padding:0 15px}.secondary{color:var(--ts-text-sky-150-12);background:var(--ts-bg-navy-850-13)}.spin{animation:spin .75s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}@media(max-width:950px){.card-actions{grid-template-columns:1fr 1fr}.permission-grid{grid-template-columns:1fr}}@media(max-width:720px){.employee-hero{align-items:flex-start;flex-direction:column;padding:19px}.primary-action{width:100%}.summary-grid{grid-template-columns:1fr}.employee-toolbar label{flex-basis:100%}.toolbar-actions{flex-direction:column;flex-basis:100%;width:100%;margin-left:0;grid-column:1/-1}.toolbar-actions button{width:100%}.employee-cards{grid-template-columns:1fr}.employee-meta{grid-template-columns:1fr}.modal-body,.modal-header,.modal-actions{padding-left:16px;padding-right:16px}.form-grid{grid-template-columns:1fr}.form-grid .full{grid-column:auto}.modal-actions button{flex:1}}@media(prefers-reduced-motion:reduce){.employee-card,.loader-ring,.spin{animation:none!important;transition:none!important}}
    .notification-bucket-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;padding:13px 16px 16px}
    .notification-bucket{display:flex;align-items:flex-start;gap:10px;padding:11px 12px;border:1px solid var(--ts-border-sky-300-a14);border-radius:11px;background:var(--ts-bg-navy-950-a18);cursor:pointer}
    .notification-bucket.enabled{border-color:var(--ts-border-blue-400-a38-2);background:var(--ts-bg-blue-400-a7)}
    .notification-bucket input{width:15px;height:15px;margin-top:2px;accent-color:var(--ts-accent-blue-400)}
    .notification-bucket div{display:grid;gap:2px;min-width:0}
    .notification-bucket strong{color:var(--ts-text-on-accent-14);font-size:.78rem}
    .notification-bucket span{color:var(--ts-text-sky-350-8);font-size:.68rem;line-height:1.4}
    @media(max-width:950px){.notification-bucket-grid{grid-template-columns:1fr}}
    .toolbar-create{min-height:41px;white-space:nowrap}
    @media(min-width:721px) and (max-width:1100px){
      .employee-cards{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
      .employee-card{padding:12px;border-radius:12px}
      .avatar{flex-basis:38px;width:38px;height:38px;border-radius:9px;font-size:.8rem}
      .role-line{margin:10px 0 8px;padding:7px 8px}
      .employee-meta{gap:6px}
      .employee-meta>div{padding:7px 8px}
      .access-summary{margin-top:8px}
      .access-summary>div{min-height:23px;margin-top:6px}
      .card-actions{gap:5px;padding-top:9px}
      .card-actions button{width:32px;height:32px;font-size:.78rem}
    }
    .view-tabs{display:flex;gap:8px}
    .view-tabs button{display:inline-flex;align-items:center;gap:7px;padding:0 16px;min-height:40px;border:1px solid var(--ts-border-navy-700-5);border-radius:9px;color:var(--ts-text-sky-300);background:var(--ts-bg-navy-900-3);font-weight:750;cursor:pointer}
    .view-tabs button.active{color:var(--ts-on-primary);background:linear-gradient(135deg,var(--ts-primary),var(--ts-bg-indigo-350-6));border-color:var(--ts-primary-border-55)}
    .owner-note{display:flex;align-items:flex-start;gap:7px;margin:12px 0 0;padding:9px 10px;border:1px solid var(--ts-border-blue-250-a20);border-radius:9px;color:var(--ts-text-sky-250-9);background:var(--ts-bg-blue-400-a6);font-size:.68rem;line-height:1.5}
  .employee-modal input.invalid, .employee-modal select.invalid, .employee-modal textarea.invalid { border-color:var(--ts-border-red-350-3)!important; box-shadow:0 0 0 4px var(--ts-shadow-red-350-a6); }
  .modal-actions{flex-wrap:nowrap;padding:14px 28px;border-top:1px solid var(--ts-border-strong)}
  .modal-actions .secondary{min-width:100px}
  .modal-actions .save-action{min-width:120px}
  .employee-overlay{position:fixed;inset:0;z-index:9000;display:grid;place-items:center;padding:var(--ts-space-4)}
  `],
})
export class EmpleadosComponent implements OnInit, OnDestroy {
  employees = signal<Employee[]>([]);
  catalog = signal<EmployeePermissionMap>({});
  templates = signal<Record<EmployeeRole, EmployeePermissionMap>>({} as Record<EmployeeRole, EmployeePermissionMap>);
  notificationBuckets = signal<NotificationBucketOption[]>([]);
  loading = signal(false);
  saving = signal(false);
  error = signal<string | null>(null);
  formError = signal<string | null>(null);
  showForm = signal(false);
  editingId = signal<number | null>(null);
  busyId = signal<number | null>(null);
  page = signal(1);
  pages = signal(0);
  total = signal(0);
  activeCount = computed(() => this.employees().filter(item => item.is_active).length);
  inactiveCount = computed(() => this.employees().filter(item => !item.is_active).length);

  search = '';
  status = '';
  readonly pageSize = 12;
  form: EmployeeForm = this.emptyForm();
  private originalAccess = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private listSubscription?: Subscription;
  private employeeAccessCache = new WeakMap<Employee, EmployeeAccessView>();

  private originalForm = '';

  viewMode = signal<'employees' | 'owners'>('employees');
  owners = signal<DelegatedOwner[]>([]);
  ownersLoading = signal(false);
  ownersError = signal<string | null>(null);
  ownerBusyId = signal<number | null>(null);
  ownersPage = signal(1);
  ownersPages = signal(0);
  ownersTotal = signal(0);
  ownerSearch = '';
  ownerStatusFilter = '';
  private ownerSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private ownersSubscription?: Subscription;

  createType = signal<CreateType>('employee');
  // Map of form fields currently invalid (used to add .invalid class and focus)
  invalidFields: Record<string, boolean> = {};

  constructor(
    private service: EmployeeService,
    public auth: AuthService,
    private dialogs: DialogService,
    private notifications: NotificationService,
    private subscriptionService: SubscriptionService,
    private cooldown: ActionCooldownService,
  ) {}

  ngOnInit(): void {
    this.service.catalog(this.businessId()).subscribe({
      next: response => {
        this.catalog.set(response.catalog);
        this.templates.set(response.role_templates);
        this.notificationBuckets.set(response.notification_buckets || []);
      },
      error: () => { this.error.set('No fue posible cargar el catálogo de permisos.'); this.notifications.error('No fue posible cargar el catálogo de permisos.'); }
    });
    this.load();
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.ownerSearchTimer) clearTimeout(this.ownerSearchTimer);
    this.listSubscription?.unsubscribe();
    this.ownersSubscription?.unsubscribe();
  }

  businessId(): number { return this.auth.currentUser()?.id || 0; }
  canCreate(): boolean { return this.auth.hasPermission('employees', 'create') && this.auth.hasPermission('employees', 'manage_permissions'); }
  canEdit(): boolean { return this.auth.hasPermission('employees', 'edit'); }
  canDelete(): boolean { return this.auth.hasPermission('employees', 'delete'); }
  canManagePermissions(): boolean { return this.auth.hasPermission('employees', 'manage_permissions'); }

  load(): void {
    if (!this.businessId()) return;
    this.listSubscription?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);
    this.listSubscription = this.service.list(
      this.businessId(),
      this.search.trim(),
      this.status,
      this.page(),
      this.pageSize,
    )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.employeeAccessCache = new WeakMap<Employee, EmployeeAccessView>();
          this.employees.set(response.data);
          this.total.set(response.total);
          this.pages.set(response.pages);
          if (response.pages > 0 && this.page() > response.pages) {
            this.page.set(response.pages);
            this.load();
          }
        },
        error: error => { const msg = friendlyError(error, 'No se pudieron cargar los sub-administradores.'); this.error.set(msg); this.notifications.error(msg); }
      });
  }

  refresh(): void {
    if (!this.cooldown.consume('empleados-refresh')) return;
    if (this.loading()) return;
    this.load();
  }

  scheduleSearch(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.load();
    }, 320);
  }

  applyFilters(): void { this.page.set(1); this.load(); }
  goTo(nextPage: number): void {
    if (nextPage < 1 || nextPage > this.pages() || nextPage === this.page()) return;
    this.page.set(nextPage);
    this.load();
  }

  setViewMode(mode: 'employees' | 'owners'): void {
    if (this.viewMode() === mode) return;
    this.viewMode.set(mode);
    if (mode === 'owners' && !this.owners().length) this.loadOwners();
  }

  loadOwners(): void {
    if (!this.businessId()) return;
    this.ownersSubscription?.unsubscribe();
    this.ownersLoading.set(true);
    this.ownersError.set(null);
    this.ownersSubscription = this.service.listOwners(
      this.businessId(), this.ownerSearch.trim(), this.ownerStatusFilter, this.ownersPage(), this.pageSize,
    )
      .pipe(finalize(() => this.ownersLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.owners.set(response.data);
          this.ownersTotal.set(response.total);
          this.ownersPages.set(response.pages);
          if (response.pages > 0 && this.ownersPage() > response.pages) {
            this.ownersPage.set(response.pages);
            this.loadOwners();
          }
        },
        error: error => { const msg = friendlyError(error, 'No se pudieron cargar los propietarios delegados.'); this.ownersError.set(msg); this.notifications.error(msg); }
      });
  }

  refreshOwners(): void {
    if (this.ownersLoading()) return;
    this.loadOwners();
  }

  scheduleOwnerSearch(): void {
    if (this.ownerSearchTimer) clearTimeout(this.ownerSearchTimer);
    this.ownerSearchTimer = setTimeout(() => {
      this.ownersPage.set(1);
      this.loadOwners();
    }, 320);
  }

  applyOwnerFilters(): void { this.ownersPage.set(1); this.loadOwners(); }
  goToOwnerPage(nextPage: number): void {
    if (nextPage < 1 || nextPage > this.ownersPages() || nextPage === this.ownersPage()) return;
    this.ownersPage.set(nextPage);
    this.loadOwners();
  }

  createTypeEyebrow(): string {
    return this.createType() === 'owner' ? 'DELEGAR PROPIETARIO' : this.createType() === 'super_admin' ? 'DELEGAR SUPERADMINISTRADOR' : 'INCORPORAR AL EQUIPO';
  }
  createTypeTitle(): string {
    return this.createType() === 'owner' ? 'Delegar propietario' : this.createType() === 'super_admin' ? 'Delegar superadministrador' : 'Nuevo sub-administrador';
  }

  onCreateTypeChange(value: CreateType): void {
    this.createType.set(value);
    if (value === 'employee') this.applyRole();
  }

  openCreate(type: CreateType = 'employee'): void {
    if (type === 'employee' && !this.canCreate()) return;
    if (type !== 'employee' && !this.auth.canManageAdmins()) return;
    if (type === 'super_admin' && this.auth.currentUser()?.role !== 'super_admin') return;
    this.editingId.set(null);
    this.createType.set(type);
    this.form = this.emptyForm();
    if (type === 'employee') this.applyRole();
    this.originalAccess = '';
    this.formError.set(null);
    this.showForm.set(true);
  }

  edit(item: Employee): void {
    if (!this.canEdit()) return;
    this.editingId.set(item.user_id);
    this.form = {
      name: item.user.name,
      last_name: '',
      email: item.user.email,
      phone_number: item.user.phone_number || '',
      job_title: item.job_title || '',
      business_role: item.business_role || 'receptionist',
      password: '',
      permissions: this.clonePermissions(item.permissions),
      // null = nunca se configuró -- se muestra como "ambos" marcados
      // porque ese es su comportamiento real (recibe todo).
      notification_categories: item.notification_categories === null ? ['reservations', 'business'] : [...item.notification_categories],
    };
    this.originalAccess = this.accessSignature(this.form.business_role, this.form.permissions);
    this.originalForm = this.employeeFormSignature(this.form);
    this.formError.set(null);
    this.showForm.set(true);
  }

  close(): void {
    if (this.saving()) return;
    this.showForm.set(false);
    this.formError.set(null);
  }

  canDelegateCurrent(): boolean {
    if (!this.canManagePermissions()) return false;
    const currentUserId = this.auth.currentUser()?.session_user?.user_id;
    return !this.editingId() || currentUserId !== this.editingId();
  }

  applyRole(): void {
    if (!this.canDelegateCurrent()) return;
    this.form.permissions = this.clonePermissions(this.templates()[this.form.business_role] || {});
  }

  async save(): Promise<void> {
    if (!this.cooldown.consume('empleados-save')) return;
    if (this.saving()) return;
    const editingId = this.editingId();
    const type = editingId ? 'employee' : this.createType();

    // Normalize and validate inputs, marking invalid fields so the template can highlight them
    this.invalidFields = {};
    const name = this.form.name.trim();
    const email = this.form.email.trim().toLowerCase();
    if (!name) this.invalidFields['name'] = true;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) this.invalidFields['email'] = true;

    if (Object.keys(this.invalidFields).length > 0) {
      this.formError.set('Por favor completa los campos marcados en rojo.');
      this.notifications.warning('Por favor completa los campos marcados en rojo.');
      this.focusFirstInvalid();
      return;
    }

    // Owner/super-admin flows use their own specialized flows (they will send reset links)
    if (type === 'owner') return this.saveOwner(name, email);
    if (type === 'super_admin') return this.saveSuperAdmin(name, email);

    const payload: EmployeeFormPayload = {
      name,
      email,
      phone_number: this.form.phone_number.trim(),
      job_title: this.form.job_title.trim(),
    };

    if (editingId && this.employeeFormSignature(this.form) === this.originalForm) {
      this.formError.set('No hay cambios por guardar en este sub-administrador.');
      this.notifications.warning('Los datos del sub-administrador no cambiaron.');
      return;
    }

    if (!editingId) {
      payload.last_name = this.form.last_name.trim();
      // No enviar contraseña: se generará automáticamente en el servidor
      payload.business_role = this.form.business_role;
      payload.permissions = this.clonePermissions(this.form.permissions);
    } else if (this.canDelegateCurrent() && this.originalAccess !== this.accessSignature(this.form.business_role, this.form.permissions)) {
      payload.business_role = this.form.business_role;
      payload.permissions = this.clonePermissions(this.form.permissions);
    }
    // A diferencia de permisos/rol, no es un cambio de seguridad -- se manda
    // siempre que haya un formulario de empleado (creación o edición), sin
    // la misma protección de auto-escalación.
    payload.notification_categories = [...this.form.notification_categories];

    this.saving.set(true);
    this.formError.set(null);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: editingId ? 'Confirmar actualización' : 'Confirmar creación',
          message: editingId ? '¿Guardas los cambios de este sub-administrador?' : '¿Creas este sub-administrador con los accesos configurados?',
          tone: 'info',
          confirmText: editingId ? 'Guardar cambios' : 'Crear acceso',
        },
        () => (editingId
          ? this.service.update(this.businessId(), editingId, payload)
          : this.service.create(this.businessId(), payload)),
        editingId ? 'Sub-administrador actualizado correctamente.' : 'Sub-administrador creado correctamente.',
        error => friendlyError(error, 'No se pudo guardar el sub-administrador.'),
      );
      if (result !== null) {
        this.showForm.set(false);
        this.load();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.saving.set(false);
    }
  }

  private async saveOwner(name: string, email: string): Promise<void> {
    this.saving.set(true);
    this.formError.set(null);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Confirmar delegación de propietario',
          message: `${name} tendrá el mismo acceso operativo que tú en este negocio (pagos, renovaciones, espacios, reservas, estadísticas). No podrá administrar accesos administrativos.`,
          tone: 'info',
          confirmText: 'Delegar propietario',
        },
        () => this.service.createOwner(this.businessId(), {
          name, last_name: '', email, phone_number: this.form.phone_number.trim(),
          job_title: this.form.job_title.trim(),
        }),
        'Propietario delegado correctamente.',
        error => friendlyError(error, 'No se pudo delegar el propietario.'),
      );
      if (result !== null) {
        this.showForm.set(false);
        this.loadOwners();
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.saving.set(false);
    }
  }

  private async saveSuperAdmin(name: string, email: string): Promise<void> {
    this.saving.set(true);
    this.formError.set(null);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Confirmar delegación de superadministrador',
          message: `${name} tendrá control administrativo de TODA la plataforma TuSala, no solo de este negocio. Se le enviará un enlace para que establezca su propia contraseña segura.`,
          tone: 'danger',
          confirmText: 'Delegar superadministrador',
        },
        () => this.subscriptionService.createManualUser({
          name, email, phone_number: this.form.phone_number.trim(), role: 'super_admin',
        }),
        'Superadministrador delegado correctamente.',
        error => friendlyError(error, 'No se pudo delegar el superadministrador.'),
      );
      if (result !== null) {
        this.showForm.set(false);
      }
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.saving.set(false);
    }
  }

  async toggle(item: Employee): Promise<void> {
    if (!this.canEdit() || this.busyId() !== null) return;
    const active = !item.is_active;
    this.busyId.set(item.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: `${active ? 'Activar' : 'Desactivar'} sub-administrador`,
          message: `¿Deseas ${active ? 'activar' : 'desactivar'} a ${item.user.name}? Las sesiones anteriores quedarán invalidadas.`,
          confirmText: active ? 'Activar' : 'Desactivar',
          tone: active ? 'info' : 'danger',
        },
        () => this.service.status(this.businessId(), item.user_id, active),
        `Sub-administrador ${active ? 'activado' : 'desactivado'} correctamente.`,
        (error: any) => friendlyError(error, 'No se pudo cambiar el estado.'),
      );
      if (result !== null) this.load();
    } catch (error: any) {
      const msg = friendlyError(error, 'No se pudo cambiar el estado.');
      this.error.set(msg);
      this.notifications.error(msg);
    } finally {
      this.busyId.set(null);
    }
  }

  async reset(item: Employee): Promise<void> {
    if (!this.canEdit() || this.busyId() !== null) return;
    this.busyId.set(item.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Enviar enlace de restablecimiento',
          message: `Se enviará un enlace por correo para que ${item.user.name} establezca su propia contraseña. ¿Confirmas enviar el enlace?`,
          confirmText: 'Enviar enlace',
          tone: 'info'
        },
        () => this.service.resetPassword(this.businessId(), item.user_id),
        'Enlace de restablecimiento enviado correctamente.',
        error => friendlyError(error, 'No se pudo enviar el enlace de restablecimiento.'),
      );
      if (result !== null) this.load();
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.busyId.set(null);
    }
  }

  async remove(item: Employee): Promise<void> {
    if (!this.canDelete() || this.busyId() !== null) return;
    this.busyId.set(item.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Eliminar acceso de sub-administrador',
          message: `${item.user.name} dejará de acceder a este negocio y sus sesiones serán invalidadas.`,
          confirmText: 'Eliminar acceso',
          danger: true,
          fields: [{ name: 'reason', label: 'Motivo', type: 'textarea', required: true, placeholder: 'Ej: Ya no forma parte del equipo del negocio' }]
        },
        values => {
          const reason = String(values['reason'] || '').trim();
          if (reason.length < 5) throw new Error('El motivo debe contener al menos 5 caracteres.');
          return this.service.delete(this.businessId(), item.user_id, reason);
        },
        'Acceso del sub-administrador eliminado correctamente.',
        error => friendlyError(error, 'No se pudo eliminar el acceso.'),
      );
      if (result !== null) this.load();
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.busyId.set(null);
    }
  }

  async toggleOwner(item: DelegatedOwner): Promise<void> {
    if (!this.auth.canManageAdmins() || this.ownerBusyId() !== null) return;
    const active = !item.is_active;
    this.ownerBusyId.set(item.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: `${active ? 'Activar' : 'Desactivar'} propietario delegado`,
          message: `¿Deseas ${active ? 'activar' : 'desactivar'} a ${item.user.name}? Las sesiones anteriores quedarán invalidadas.`,
          confirmText: active ? 'Activar' : 'Desactivar',
          tone: active ? 'info' : 'danger',
        },
        () => this.service.ownerStatus(this.businessId(), item.user_id, active),
        `Propietario delegado ${active ? 'activado' : 'desactivado'} correctamente.`,
        (error: any) => friendlyError(error, 'No se pudo cambiar el estado.'),
      );
      if (result !== null) this.loadOwners();
    } catch (error: any) {
      const msg = friendlyError(error, 'No se pudo cambiar el estado.');
      this.ownersError.set(msg);
      this.notifications.error(msg);
    } finally {
      this.ownerBusyId.set(null);
    }
  }

  async resetOwnerPwd(item: DelegatedOwner): Promise<void> {
    if (!this.auth.canManageAdmins() || this.ownerBusyId() !== null) return;
    this.ownerBusyId.set(item.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Enviar enlace de restablecimiento',
          message: `Se enviará un enlace por correo para que ${item.user.name} establezca su propia contraseña. ¿Confirmas enviar el enlace?`,
          confirmText: 'Enviar enlace',
          tone: 'info'
        },
        () => this.service.resetOwnerPassword(this.businessId(), item.user_id),
        'Enlace de restablecimiento enviado correctamente.',
        error => friendlyError(error, 'No se pudo enviar el enlace de restablecimiento.'),
      );
      if (result !== null) this.loadOwners();
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.ownerBusyId.set(null);
    }
  }

  async removeOwner(item: DelegatedOwner): Promise<void> {
    if (!this.auth.canManageAdmins() || this.ownerBusyId() !== null) return;
    this.ownerBusyId.set(item.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Eliminar acceso de propietario delegado',
          message: `${item.user.name} dejará de acceder a este negocio y sus sesiones serán invalidadas.`,
          confirmText: 'Eliminar acceso',
          danger: true,
          fields: [{ name: 'reason', label: 'Motivo', type: 'textarea', required: true, placeholder: 'Ej: Ya no forma parte del equipo del negocio' }]
        },
        values => {
          const reason = String(values['reason'] || '').trim();
          if (reason.length < 5) throw new Error('El motivo debe contener al menos 5 caracteres.');
          return this.service.deleteOwner(this.businessId(), item.user_id, reason);
        },
        'Acceso del propietario delegado eliminado correctamente.',
        error => friendlyError(error, 'No se pudo eliminar el acceso.'),
      );
      if (result !== null) this.loadOwners();
    } catch {
      // El error ya se muestra en el modal de resultado (confirmAndRun).
    } finally {
      this.ownerBusyId.set(null);
    }
  }

  trackOwner(_index: number, item: DelegatedOwner): number { return item.user_id; }

  moduleNames(): string[] { return Object.keys(this.catalog()); }
  selected(module: string, action: string): boolean { return (this.form.permissions[module] || []).includes(action); }
  moduleEnabled(module: string): boolean { return !!this.form.permissions[module]?.length; }

  togglePermission(module: string, action: string, enabled: boolean): void {
    const actions = new Set(this.form.permissions[module] || []);
    if (action === 'view' && !enabled) {
      actions.clear();
    } else {
      enabled ? actions.add(action) : actions.delete(action);
      if (enabled && action !== 'view' && this.catalog()[module]?.includes('view')) actions.add('view');
    }
    this.form.permissions[module] = [...actions].filter(item => this.catalog()[module]?.includes(item));
  }

  toggleModule(module: string): void {
    this.form.permissions[module] = this.moduleEnabled(module) ? [] : [...(this.catalog()[module] || [])];
  }

  permissionCount(permissions: EmployeePermissionMap): number {
    return Object.entries(permissions || {}).reduce((total, [module, actions]) =>
      total + actions.filter(action => this.catalog()[module]?.includes(action)).length, 0);
  }

  enabledModules(permissions: EmployeePermissionMap): Array<{ key: string; label: string }> {
    return Object.keys(permissions || {})
      .filter(key => permissions[key]?.some(action => this.catalog()[key]?.includes(action)))
      .map(key => ({ key, label: this.moduleLabel(key) }));
  }

  employeeAccess(employee: Employee): EmployeeAccessView {
    const cached = this.employeeAccessCache.get(employee);
    if (cached) return cached;
    const view = {
      count: this.permissionCount(employee.permissions),
      modules: this.enabledModules(employee.permissions),
    };
    this.employeeAccessCache.set(employee, view);
    return view;
  }

  trackModule(_index: number, module: { key: string }): string { return module.key; }

  criticalAction(action: string): boolean {
    return ['delete', 'restore', 'permanent_delete', 'manage_permissions'].includes(action);
  }

  initials(name = ''): string {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'SA';
  }

  roleLabel(role: EmployeeRole): string {
    return ({ manager: 'Gerente', receptionist: 'Recepción', analyst: 'Analista' } as Record<EmployeeRole, string>)[role] || role;
  }

  moduleLabel(module: string): string {
    return ({
      spaces: 'Espacios',
      reservations: 'Reservas y calendario',
      customers: 'Clientes',
      statistics: 'Estadísticas',
      settings: 'Configuración',
      employees: 'Sub-Administradores',
      recycle: 'Papelera',
    } as Record<string, string>)[module] || module.replace(/_/g, ' ');
  }

  moduleIcon(module: string): string {
    return ({
      spaces: 'bi bi-door-open',
      reservations: 'bi bi-calendar2-check',
      customers: 'bi bi-people',
      statistics: 'bi bi-bar-chart-line',
      settings: 'bi bi-gear',
      employees: 'bi bi-person-gear',
      recycle: 'bi bi-recycle',
    } as Record<string, string>)[module] || 'bi bi-box';
  }

  actionLabel(action: string): string {
    return ({
      view: 'Consultar',
      create: 'Crear',
      edit: 'Editar',
      delete: 'Enviar a la Papelera',
      restore: 'Restaurar',
      permanent_delete: 'Eliminar permanentemente',
      export: 'Exportar',
      manage_permissions: 'Delegar permisos',
    } as Record<string, string>)[action] || action.replace(/_/g, ' ');
  }

  trackEmployee(_index: number, item: Employee): number { return item.user_id; }

  private emptyForm(): EmployeeForm {
    // Los dos grupos marcados por defecto: mismo comportamiento que ya
    // existía antes de este selector (recibía todos los avisos).
    return { name: '', last_name: '', email: '', phone_number: '', job_title: '', business_role: 'receptionist', password: '', permissions: {}, notification_categories: ['reservations', 'business'] };
  }

  private clonePermissions(permissions: EmployeePermissionMap): EmployeePermissionMap {
    return Object.fromEntries(Object.entries(permissions || {}).map(([module, actions]) => [module, [...actions]]));
  }

  private accessSignature(role: EmployeeRole, permissions: EmployeePermissionMap): string {
    const normalized = Object.keys(permissions || {}).sort().reduce((result, module) => {
      result[module] = [...permissions[module]].sort();
      return result;
    }, {} as EmployeePermissionMap);
    return JSON.stringify({ role, permissions: normalized });
  }

  private employeeFormSignature(form: EmployeeForm): string {
    return JSON.stringify({
      name: form.name.trim(), email: form.email.trim().toLowerCase(), phone_number: form.phone_number.trim(),
      job_title: form.job_title.trim(), access: this.accessSignature(form.business_role, form.permissions),
      notification_categories: [...form.notification_categories].sort(),
    });
  }

  notificationBucketSelected(bucket: NotificationBucket): boolean {
    return this.form.notification_categories.includes(bucket);
  }

  toggleNotificationBucket(bucket: NotificationBucket, enabled: boolean): void {
    const set = new Set(this.form.notification_categories);
    if (enabled) set.add(bucket); else set.delete(bucket);
    this.form.notification_categories = [...set];
  }

  /**
   * Scroll to and focus the first invalid input inside the modal so the user sees the problem.
   */
  private focusFirstInvalid(): void {
    // Allow Angular to render the invalid classes first
    setTimeout(() => {
      const modal = document.querySelector('.employee-modal');
      if (!modal) return;
      const first = modal.querySelector('.invalid') as HTMLElement | null;
      if (first) {
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { (first as HTMLElement).focus(); } catch {}
      }
    }, 50);
  }

}
